import { useState, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '@context/AuthContext';
import { useTwilioDevice } from '@hooks/useTwilioDevice';
import { useCallState } from '@hooks/useCallState';
import { hangupAll, toggleMute, sendDigits, answerCall, rejectCall } from '@services/twilioService';
import { sendToVoicemail, hangUpCall } from '@services/api';
import { parseApiError } from '@utils/errorUtils';
import TabNavigation from '@components/TabNavigation/TabNavigation';
import ActiveCallBar from '@components/CallControls/ActiveCallBar';
import IncomingCallBar from '@components/CallControls/IncomingCallBar';
import PhoneControlsTab from '@components/Dashboard/tabs/PhoneControlsTab';
import OutreachSearchTab from '@components/Dashboard/tabs/OutreachSearchTab';
import ErrorsTab from '@components/Dashboard/tabs/ErrorsTab';
import VoiceTemplatesTab from '@components/Dashboard/tabs/VoiceTemplatesTab';
import IvrQuestionsTab from '@components/Dashboard/tabs/IvrQuestionsTab';
import IvrFlowTab from '@components/Dashboard/tabs/IvrFlowTab';
import './Dashboard.css';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const {
    activeCall,
    incomingCall,
    callStatus,
    callDirection,
    isMuted,
    setIsMuted,
    callDuration,
    callConnection,
    setCallConnection,
    startOutgoingCall,
    startIncomingCall,
    receiveIncomingCall,
    endCall,
  } = useCallState();
  const [activeTab, setActiveTab] = useState('phone');
  const [selectedQueue, setSelectedQueue] = useState('');
  const [callingNumber, setCallingNumber] = useState('');
  const [sendingVoicemail, setSendingVoicemail] = useState(false);
  const [callError, setCallError] = useState('');
  // Caller number from the /api/queue/answer response, held until the matching inbound leg
  // arrives. The response always precedes Twilio dialing this client, so it is in place in time.
  const [pendingInboundFrom, setPendingInboundFrom] = useState('');

  const handleCallEnded = useCallback(() => {
    setCallingNumber('');
    setPendingInboundFrom('');
    setSendingVoicemail(false);
    setCallError('');
    endCall();
  }, [endCall]);

  // Shared by auto-answer and the manual Answer button.
  const acceptIncomingCall = useCallback((call, from) => {
    try {
      answerCall(call);
    } catch (err) {
      setCallError('Failed to answer call: ' + (err?.message ?? 'unknown'));
      return;
    }

    setCallConnection(call);
    startIncomingCall(from);
  }, [setCallConnection, startIncomingCall]);

  const handleIncomingCall = useCallback((call) => {
    // Twilio has no server-side auto-answer. The <Parameter name="auto_answer" value="true"/>
    // that the queue connect TwiML emits (QueueCallService.GetConnectTwimlAsync) is purely an
    // application convention — this client has to honor it by calling accept(), or the caller
    // hears ringback until the <Dial timeout="60"> expires and is then dropped.
    const autoAnswer = call?.customParameters?.get('auto_answer') === 'true';

    // Prefer the queue answer response: it carries the caller number straight from the contact
    // record, whereas the leg's own From falls back to the team's outbound number when the
    // caller withheld their caller ID (see QueueCallService.GetConnectTwimlAsync).
    const from = pendingInboundFrom || call?.parameters?.From || '';

    call.on('disconnect', handleCallEnded);
    call.on('cancel', handleCallEnded);
    call.on('reject', handleCallEnded);
    call.on('error', (err) => {
      console.error('[Dashboard] Inbound call error:', err);
      // Tear down first — handleCallEnded clears callError, so setting it after is what
      // actually leaves the message on screen.
      handleCallEnded();
      setCallError('Call error: ' + (err?.message ?? 'unknown'));
    });

    setCallError('');
    setCallingNumber(from);
    setPendingInboundFrom('');

    if (!autoAnswer) {
      // Ring and let the coach decide. The leg stays open until they act or Twilio's
      // <Dial timeout> cancels it (which fires 'cancel' above).
      receiveIncomingCall(call);
      return;
    }

    acceptIncomingCall(call, from);
  }, [acceptIncomingCall, handleCallEnded, pendingInboundFrom, receiveIncomingCall]);

  const handleAnswerIncoming = useCallback(() => {
    if (!incomingCall) return;
    acceptIncomingCall(incomingCall, callingNumber);
  }, [acceptIncomingCall, callingNumber, incomingCall]);

  const handleRejectIncoming = useCallback(() => {
    if (!incomingCall) return;
    try {
      rejectCall(incomingCall);
    } catch (err) {
      // Already gone (caller hung up mid-click) — still clear the UI.
      console.error('[Dashboard] Reject failed:', err);
    }
    handleCallEnded();
  }, [handleCallEnded, incomingCall]);

  const { device, isReady, isLoading, error } = useTwilioDevice({
    onIncomingCall: handleIncomingCall,
  });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleCallStarted = useCallback((phoneNumber) => {
    setCallingNumber(phoneNumber);
    startOutgoingCall(phoneNumber);
  }, [startOutgoingCall]);

  const handleCallConnectionReady = useCallback((connection) => {
    setCallConnection(connection);
  }, [setCallConnection]);

  const handleToggleMute = useCallback(() => {
    if (!callConnection) return;
    const newMuted = toggleMute(callConnection);
    setIsMuted(newMuted);
  }, [callConnection, setIsMuted]);

  const handleSendDigits = useCallback((digits) => {
    if (!callConnection) return;
    sendDigits(callConnection, digits);
  }, [callConnection]);

  const handleHangup = useCallback(() => {
    // Disconnect the Twilio SDK call immediately so the UI clears without waiting for the network.
    try {
      hangupAll();
    } catch (err) {
      console.error('[Dashboard] SDK hangup failed:', err);
    }

    // Notify the backend to terminate via Twilio REST API and record the end time.
    // Fire-and-forget — we don't block the UI on this.
    const callSid = callConnection?.parameters?.CallSid;
    if (callSid) {
      hangUpCall(callSid).catch((err) => {
        console.error('[Dashboard] Backend hang-up failed (call already ended or network error):', err);
      });
    }

    handleCallEnded();
  }, [callConnection, handleCallEnded]);

  const handleSendToVoicemail = useCallback(async () => {
    if (!callConnection) {
      setCallError('No active call to send to voicemail');
      return;
    }

    try {
      setSendingVoicemail(true);
      setCallError('');

      const callSid = callConnection.parameters?.CallSid;
      if (!callSid) {
        setCallError('Cannot determine call SID');
        setSendingVoicemail(false);
        return;
      }

      await sendToVoicemail(callSid);

      hangupAll();
      handleCallEnded();
    } catch (err) {
      setCallError('Failed to send voicemail: ' + parseApiError(err));
      setSendingVoicemail(false);
    }
  }, [callConnection, handleCallEnded]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'phone':
        return (
          <PhoneControlsTab
            device={device}
            isReady={isReady}
            isLoading={isLoading}
            error={error}
            activeCall={activeCall}
            callStatus={callStatus}
            onCallStarted={handleCallStarted}
            onCallEnded={handleCallEnded}
            onCallConnectionReady={handleCallConnectionReady}
            selectedQueue={selectedQueue}
            onQueueSelected={setSelectedQueue}
            onQueueCallAnswered={setPendingInboundFrom}
            defaultTestPatientGuid={user?.defaultTestPatientGuid}
          />
        );
      case 'outreach-search':
        return <OutreachSearchTab />;
      case 'errors':
        return <ErrorsTab />;
      case 'voicemail':
        return <VoiceTemplatesTab />;
      case 'ivr-questions':
        return <IvrQuestionsTab />;
      case 'ivr-flow':
        return <IvrFlowTab />;
      default:
        return (
          <PhoneControlsTab
            device={device}
            isReady={isReady}
            isLoading={isLoading}
            error={error}
            activeCall={activeCall}
            callStatus={callStatus}
            onCallStarted={handleCallStarted}
            onCallEnded={handleCallEnded}
            onCallConnectionReady={handleCallConnectionReady}
            selectedQueue={selectedQueue}
            onQueueSelected={setSelectedQueue}
            onQueueCallAnswered={setPendingInboundFrom}
            defaultTestPatientGuid={user?.defaultTestPatientGuid}
          />
        );
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <h1>S3 Phone System</h1>
        <div className="header-right">
          <div className="device-status">
            {isLoading && <span className="status-badge loading">Initializing...</span>}
            {isReady && (
              <span className="status-badge ready">
                <span className="status-dot"></span> Ready
              </span>
            )}
            {error && <span className="status-badge error">Error: {error.message || 'Unknown'}</span>}
            {!isLoading && !isReady && !error && <span className="status-badge loading">Waiting...</span>}
          </div>
          <div className="user-menu">
            {user && <span className="user-name">{user.email}</span>}
            <button onClick={handleLogout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      {callStatus === 'ringing' && (
        <IncomingCallBar
          phoneNumber={callingNumber}
          onAnswer={handleAnswerIncoming}
          onReject={handleRejectIncoming}
        />
      )}

      {callStatus === 'active' && (
        <ActiveCallBar
          phoneNumber={callingNumber}
          direction={callDirection}
          callDuration={callDuration}
          isMuted={isMuted}
          onToggleMute={handleToggleMute}
          onSendDigits={handleSendDigits}
          onSendToVoicemail={handleSendToVoicemail}
          sendingVoicemail={sendingVoicemail}
          onHangup={handleHangup}
        />
      )}

      {callError && (
        <div className="active-call-bar-error">{callError}</div>
      )}

      <div className="dashboard-container">
        <TabNavigation activeTab={activeTab} onTabChange={setActiveTab} />
        <main className="dashboard-content">
          {renderTabContent()}
        </main>
      </div>

      {/* Debug Info */}
      <div className="debug-info">
        <details>
          <summary>Device Debug Info</summary>
          <div className="debug-content">
            <p><strong>Device Ready:</strong> {isReady ? 'Yes' : 'No'}</p>
            <p><strong>Device Loading:</strong> {isLoading ? 'Yes' : 'No'}</p>
            <p><strong>Device Object:</strong> {device ? 'Initialized' : 'Not initialized'}</p>
            <p><strong>Error:</strong> {error ? error.message || JSON.stringify(error) : 'None'}</p>
          </div>
        </details>
      </div>

      <footer className="dashboard-footer">
        <p>S3 Phone System Dashboard v1.0.0</p>
      </footer>
    </div>
  );
}
